import type { NotificationContact } from './orderNotifications';
// Order management TypeScript types

export type OrderStatus =
  | 'draft'
  | 'submission_failed'
  | 'submitted_to_sage'
  | 'confirmed_in_sage'
  | 'accepted'
  | 'shipped'
  | 'order_completed'
  | 'cancelled';

export type OrderSourceType = 'email_llm' | 'manual' | 'quote_conversion';

export type TransitionTriggerType = 'user' | 'system' | 'llm' | 'sage_callback';

// Which extracted line field the deterministic pipeline rewrote.
export type RewriteField = 'item_code' | 'quantity';

// Severity of a recorded pipeline rewrite. Quantity/UOM conversions are
// WARNING; an item-code rewrite is CRITICAL. Mirrors RewriteSeverity in
// services/orders/rewrite_audit.py.
export type RewriteSeverity = 'info' | 'warning' | 'critical';

// Severity tier of a per-line needs_review flag. BLOCKER cannot round-trip
// to Sage as-is; VERIFY needs a human answer; INFO is corroborated-safe
// noise kept for audit only. Mirrors ReviewTier in
// services/orders/review_tiering.py.
export type ReviewTier = 'BLOCKER' | 'VERIFY' | 'INFO';

// How the CSR answered a line's review card: 'accepted' (extraction was
// right), 'fixed' (CSR changed a value), or 'sent_as_is' (explicit per-line
// override). Null until resolved.
export type ReviewResolution = 'accepted' | 'fixed' | 'sent_as_is';

/**
 * Audit evidence for a review-tier decision: which rule fired plus the
 * inputs it saw (raw unit token, catalog UOM, producer reason). Mirrors
 * TierDecision.to_evidence_dict() in services/orders/review_tiering.py.
 */
export interface ReviewEvidence {
  rule?: string;
  [key: string]: unknown;
}

/**
 * One deterministic rewrite the order pipeline applied to an LLM-extracted
 * line between extraction and the persisted line rows (e.g. the UOM
 * normalizer converting "40 GAL" of a 4-gal kit to "10 KIT"). Recorded on the
 * order at creation so salespeople can verify what changed. Mirrors the
 * serialized `PipelineRewrite` from services/orders/rewrite_audit.py.
 */
export interface MerlinAudit {
  /** Agent-console conversation with Merlin's full extraction transcript. */
  conversation_id?: string | null;
  cost_usd?: number | null;
  [key: string]: unknown;
}

/** Merlin's "this looks like a NEW customer" block — prefill for the
 *  Create-in-Sage modal. Mirrors ProposedNewCustomer in
 *  services/orders/proposals.py. */
export interface ProposedNewCustomer {
  name: string;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
  email?: string | null;
  contact_name?: string | null;
  evidence?: string | null;
}

/** Merlin's "this ships to a NEW address" block — prefill for
 *  NewSageShipToForm. Mirrors ProposedNewShipTo in
 *  services/orders/proposals.py. */
export interface ProposedNewShipTo {
  name?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  evidence?: string | null;
}

export interface LlmExtractionData {
  extractor?: string | null;
  extractor_model?: string | null;
  extraction_concerns?: string[] | null;
  merlin_audit?: MerlinAudit | null;
  proposed_new_customer?: ProposedNewCustomer | null;
  proposed_new_ship_to?: ProposedNewShipTo | null;
  /**
   * Stamped by the hourly unknown-customer relink sweep once it bound the
   * Sage account the desk created after the draft: the match evidence
   * (customer_no, name score, corroborations) and the previous header values.
   */
  customer_relink?: Record<string, unknown> | null;
  /**
   * Stamped by the hourly draft-item-resolution sweep once it wrote a Sage item
   * created after the draft onto a BLOCKER "no item code" line: which lines,
   * which codes, how many blockers remain (kanban 9c2ab40c).
   */
  item_resolution?: ItemResolutionEvidence | null;
  /** Customer-stated required/delivery date the extractor read (ISO) and the PO label+value it came from. */
  udf_required_by?: string | null;
  udf_required_by_evidence?: string | null;
  /** Ship-from warehouse Merlin proposed (3-char Sage code) and the PO text / history fact he read it from. */
  warehouse_code?: string | null;
  warehouse_code_evidence?: string | null;
  /** Gaps the extractor left that a deterministic rule filled — `detail` is the CSR-readable provenance. */
  pipeline_filled_fields?: PipelineFieldAuditRow[] | null;
  /** Per-warehouse will-call invoice counts the bridge read when it filled / questioned the ship-from (kanban b5ba3619). */
  will_call_warehouse_history?: WarehouseHistoryRow[] | null;
  /** Per-item shipped-from history (item_code → per-warehouse line counts) behind an item-history fill or concern (kanban 29601f9c). */
  item_warehouse_history?: Record<string, WarehouseHistoryRow[]> | null;
  /** Extractor values the pipeline could not keep (dev-side audit; the field was left empty). */
  pipeline_dropped_fields?: PipelineFieldAuditRow[] | null;
  [key: string]: unknown;
}

/** One line the draft-item-resolution sweep rewrote (services/orders/draft_item_resolution.py). */
export interface ItemResolutionEntry {
  line_id: string;
  position?: number | null;
  item_code: string;
  previous_description?: string | null;
  product_created_at?: string | null;
  resolved_at?: string | null;
}

export interface ItemResolutionEvidence {
  last_resolved_at?: string | null;
  resolved: ItemResolutionEntry[];
  remaining_blocked: number;
  ready_to_key: boolean;
}

/** One warehouse's share of a shipped history (services/orders/warehouse_resolution.py). */
export interface WarehouseHistoryRow {
  warehouse_code?: string | null;
  invoices?: number | null;
  last_invoice_date?: string | null;
}

/** One row of pipeline_filled_fields / pipeline_dropped_fields (services/orders/extraction_advisory.py). */
export interface PipelineFieldAuditRow {
  field: string;
  value: string;
  reason: string;
  stage: string;
  detail: string;
}

export interface PipelineRewriteRecord {
  line_index: number;
  item_code: string;
  field: RewriteField;
  before: string;
  after: string;
  rule: string;
  severity: RewriteSeverity;
}

export interface OrderRecord {
  id: string;
  status: OrderStatus;
  source_type: OrderSourceType;
  source_email_id: string | null;
  // Source email clocks (list/detail). source_email_late_sync_days is set when
  // the email reached our store days after it was sent (a catch-up sync), so
  // the "Date" column can say "Received Jul 30 · synced Sep 25" instead of
  // passing an old PO off as new.
  source_email_received_at?: string | null;
  source_email_synced_at?: string | null;
  source_email_late_sync_days?: number | null;
  source_thread_id: string | null;
  customer_no: string;
  customer_name: string;
  salesperson_no: string;
  salesperson_name: string;
  subtotal: string; // Decimal as string
  freight_cost: string | null;
  total: string | null;
  payment_terms: string | null;
  po_number: string | null;
  requested_ship_date: string | null;
  requested_ship_date_confirmed: boolean;
  udf_jobsite: string; // "Y" or "N"
  order_type: string; // Sage OrderType: "S" standard or "B" backorder
  backorder_of_order_id?: string | null; // set when this draft backs another order
  warehouse_code: string; // 3-char code, default "000"
  ship_via: string | null; // Sage ShipVia carrier code
  udf_bl: string; // Bill of lading: "ppd.", "col.", or "3rd party"
  // Bill freight to the customer at invoicing. false = waived: a
  // DO NOT BILL FREIGHT /C instruction is kept on the Sage order.
  // Optional so pre-existing fixtures/payloads without it read as true.
  charge_freight?: boolean;
  udf_quote: string | null;
  udf_quote_2: string | null;
  udf_quote_3: string | null;
  udf_quote_4: string | null;
  udf_quote_5: string | null;
  cancel_reason_code: string | null; // Sage CancelReasonCode — places order on hold when set
  sage_order_no: string | null;
  ship_to_address: Record<string, unknown> | null;
  /**
   * Buyer/branch contact(s) the CSR picked for THIS order — they receive the
   * confirmation at Sage submit and the invoice copy once invoiced
   * (kanban 67b70825). Optional so older fixtures/mocks stay valid.
   */
  notification_contacts?: NotificationContact[] | null;
  freight_quote_id: string | null;
  sage_submitted_at: string | null;
  sage_confirmed_at: string | null;
  // When the warehouse scanned the BOL at shipping (bol_scan mirror stamp).
  shipped_at?: string | null;
  sage_detected: boolean | null;
  sage_detected_at: string | null;
  llm_confidence: number | null;
  llm_model_version: string | null;
  // Deterministic AI extraction confidence bucket, joined from the linked
  // email classification. NULL on manual orders and on email-sourced
  // orders written before the bucket rollout (no historical backfill).
  confidence_bucket: 'high' | 'medium' | 'low' | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Derived flags enriched from the linked sage_sales_orders row.
  // True when the sync detected that the Sage production SO vanished
  // from SO_SalesOrderHeader (see delete_detection.py).
  deleted_in_sage?: boolean;
  deleted_in_sage_detected_at?: string | null;
  // Deterministic line rewrites the pipeline applied at order creation
  // (item-code or UOM quantity conversions). NULL/empty when nothing was
  // rewritten. Surfaced read-only in the order detail view as an informational
  // notice so salespeople can see what was automatically changed.
  pipeline_rewrites?: PipelineRewriteRecord[] | null;
  // The customer / PO the extraction produced (null on hand-keyed orders)
  // and whether the CSR ruled the draft a DIFFERENT order in the portal's
  // header-identity warning (kanban 3019f212).
  extracted_customer_no?: string | null;
  extracted_po_number?: string | null;
  source_identity_detached_at?: string | null;
  source_identity_detach_reason?: string | null;
  llm_extraction_data?: LlmExtractionData | null;
  // Count of unresolved BLOCKER/VERIFY review lines on this order, enriched
  // on GET /portal/orders list rows only. NULL when not enriched (detail
  // endpoint, non-list contexts).
  attention_count?: number | null;
  /**
   * Lines with no unit_price, counted per page by the API (not persisted).
   * The subtotal leaves those lines out, so the list row and the modal
   * header show "N lines unpriced" next to the amount when this is > 0.
   * Undefined/null when the endpoint did not compute it.
   */
  unpriced_line_count?: number | null;
  // Parked-draft marker (2026-09-24 order portal review): the desk left this
  // draft un-keyed on purpose. All null when not parked. Optional so older
  // fixtures still type-check.
  parked_reason?: DraftParkReason | null;
  parked_note?: string | null;
  parked_by?: string | null;
  parked_at?: string | null;
  /** Optional come-back date (ISO date); the saved alerts turn red again after it. */
  parked_until?: string | null;
  // Due-date urgency (kanban 3d77f755), enriched on GET /portal/orders list
  // rows for draft-lane orders only: the customer-requested ship / pickup
  // date has passed ("overdue"), is today ("due_today") or is the next
  // business day ("due_soon"). Null when not at risk, parked, past the
  // draft lane, or not enriched. due_business_days is signed (negative =
  // overdue). is_will_call = the customer collects the order (ship_via WC).
  due_urgency?: DueUrgency | null;
  due_date?: string | null;
  due_business_days?: number | null;
  is_will_call?: boolean | null;
  // How the desk answered this cycle's alert (owner ruling 2026-10-01):
  // 'acknowledged' = Acknowledge on the red bar ("I've got this"),
  // 'saved_for_later' = parked on the bar until due_desk_until. Null = open.
  due_desk_state?: DueDeskState | null;
  due_desk_by?: string | null;
  due_desk_until?: string | null;
}

export type DueDeskState = 'acknowledged' | 'saved_for_later';

/** Mirrors DueStage in services/orders/draft_due.py. */
export type DueUrgency = 'overdue' | 'due_today' | 'due_soon';

/** Mirrors DraftParkReason in db/models/orders.py. */
export type DraftParkReason =
  | 'waiting_on_customer'
  | 'waiting_on_rep'
  | 'waiting_on_credit'
  | 'save_for_later';

export const PARK_REASON_LABELS: Record<DraftParkReason, string> = {
  waiting_on_customer: 'Waiting on customer',
  waiting_on_rep: 'Waiting on salesperson',
  waiting_on_credit: 'Waiting on credit / account set-up',
  save_for_later: 'Saved for later',
};

/**
 * The "Unknown customer" cohort: a draft Merlin could not bind to a Sage
 * account (customer_no empty) that carries his proposed_new_customer block.
 * Mirrors services/orders/unknown_customer_relink.is_awaiting_sage_account.
 */
export function isAwaitingSageAccount(
  order: Pick<OrderRecord, 'status' | 'customer_no' | 'llm_extraction_data'>,
): boolean {
  if (order.status !== 'draft') return false;
  if (order.customer_no && order.customer_no.trim()) return false;
  const name = order.llm_extraction_data?.proposed_new_customer?.name;
  return typeof name === 'string' && name.trim().length > 0;
}

/**
 * A draft on which the hourly sweep wrote at least one Sage item that was
 * created after the draft (the rep's order waited on item setup). Mirrors
 * services/orders/draft_item_resolution.stamp_order_evidence.
 */
export function hasResolvedItems(
  order: Pick<OrderRecord, 'status' | 'llm_extraction_data'>,
): boolean {
  if (order.status !== 'draft') return false;
  const resolved = order.llm_extraction_data?.item_resolution?.resolved;
  return Array.isArray(resolved) && resolved.length > 0;
}

export function isParked(order: Pick<OrderRecord, 'parked_reason'>): boolean {
  return !!order.parked_reason;
}

export interface OrderLineItem {
  id: string;
  order_id: string;
  position: number;
  item_code: string;
  item_description: string;
  product_line: string | null;
  quantity: string | null;
  unit_price: string | null;
  /**
   * LLM-extracted PO unit price, captured at detection time and
   * immutable thereafter. When this differs from `unit_price` the
   * portal renders a "PO says $X" mismatch warning so the operator
   * can see what the PO said vs. what will ship. Null for manually
   * added line items (no PO reading).
   */
  extracted_price: string | null;
  discount_percent: string | null;
  line_total: string | null;
  packaging: string | null;
  notes: string | null;
  /**
   * Sage-bound per-line comment (written to Sage's CommentText on
   * submission). Distinct from `notes`, which stays internal.
   */
  sage_comment_text: string | null;
  llm_confidence: number | null;
  /**
   * LLM-extracted quantity in the customer's original UOM, before any
   * kit/pail conversion. Immutable evidence for the review answer panel
   * ("Customer wrote: 40 GAL"). Null for manually added lines.
   */
  raw_quantity?: string | null;
  /** UOM token as the customer wrote it on the PO (e.g. 'GAL', 'PAIL'). */
  raw_unit?: string | null;
  /**
   * True when the agent (or downstream validation) flagged this line as
   * requiring human review before Sage submission.
   */
  needs_review?: boolean;
  /** Short, specific reason this line needs review. */
  needs_review_reason?: string | null;
  /**
   * Severity of the needs_review flag. The detail endpoint always resolves
   * this for flagged lines (legacy rows are classified at serve time), so
   * in the order modal: flagged line ⇒ review_tier is set.
   */
  review_tier?: ReviewTier | null;
  /** Audit evidence for the tier decision (rule that fired + inputs). */
  review_evidence?: ReviewEvidence | null;
  /**
   * The flag reason split into its producer messages, each tiered
   * independently (serve-time enrichment from the detail endpoint). The
   * answer card renders these as separate bullets, muting INFO-class
   * fragments. Falls back to needs_review_reason when absent.
   */
  review_reason_parts?: { text: string; tier: ReviewTier }[] | null;
  /** How the CSR answered this line's review card. Null until resolved. */
  review_resolution?: ReviewResolution | null;
  /**
   * The CSR's latest note on why this line is priced away from the PO price
   * (serve-time, from the price_change_reason audit rows; owner ask
   * 2026-10-29). Null/absent when no note was left.
   */
  price_change_reason?: string | null;
  created_at: string;
  updated_at: string;
}

// Submission field validation error from the API
export interface SubmissionFieldError {
  field: string;
  message: string;
  current_value?: string | null;
  line_item_position?: number | null;
}

export interface OrderStateTransition {
  id: string;
  order_id: string;
  from_status: OrderStatus;
  to_status: OrderStatus;
  triggered_by: string | null;
  trigger_type: TransitionTriggerType;
  notes: string | null;
  transition_metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface OrderListResponse {
  orders: OrderRecord[];
  total: number;
  limit: number;
  offset: number;
}

export interface OrderDetailResponse {
  order: OrderRecord;
  line_items: OrderLineItem[];
  history: OrderStateTransition[];
  allowed_transitions: string[];
}

export interface TransitionRequest {
  action: string;
  notes?: string;
  reason?: string;
  sage_order_no?: string;
}

export interface ManualOrderRequest {
  customer_no: string;
  customer_name: string;
  salesperson_no: string;
  salesperson_name: string;
  payment_terms?: string;
  ship_to_address?: Record<string, unknown>;
}

export interface PortalManualOrderRequest {
  customer_no: string;
  customer_name: string;
  salesperson_no?: string;
  salesperson_name?: string;
  payment_terms?: string;
  ship_to_address?: Record<string, unknown>;
  po_number?: string;
  requested_ship_date?: string;
  requested_ship_date_confirmed?: boolean;
  udf_jobsite?: string;
  order_type?: 'S' | 'B';
  warehouse_code?: string;
  udf_bl?: string;
  /** Bill freight to the customer at invoicing (default true). */
  charge_freight?: boolean;
  cancel_reason_code?: string;
  /**
   * Internal FK to sales_quotes.id when the order is being created from a
   * selected sales quote. The backend will auto-derive udf_quote from the
   * referenced quote's source_id if udf_quote is not explicitly provided.
   */
  sales_quote_id?: string;
  /**
   * External Sage quote number (UDF_QUOTE). Set this to override the
   * auto-derived value from sales_quote_id, or when linking to an external
   * quote that doesn't exist in our database.
   */
  udf_quote?: string;
}

// Cancel reason code lookup
export interface CancelReasonCode {
  cancel_reason_code: string;
  description: string | null;
}

export interface LineItemCreateRequest {
  item_code: string;
  item_description: string;
  product_line?: string;
  quantity: string;
  unit_price: string;
  discount_percent?: string;
  packaging?: string;
  notes?: string;
}

// Sage tracking types
export interface SageSubmissionInfo {
  sage_order_no: string | null;
  sage_response: Record<string, unknown> | null;
  latency_ms: number | null;
  submitted_at: string | null;
  submitted_by: string | null;
  transition_notes: string | null;
}

export interface SageValidationInfo {
  found: boolean;
  table_name: string | null;
  order_data: Record<string, unknown> | null;
  error: string | null;
}

export interface SageTrackingResponse {
  order_id: string;
  order_status: string;
  sage_order_no: string | null;
  submission: SageSubmissionInfo | null;
  validation: SageValidationInfo | null;
}

// Status display helpers
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  draft: 'Draft',
  submission_failed: 'Submission Failed',
  submitted_to_sage: 'Submitted to Sage',
  confirmed_in_sage: 'Confirmed in Sage',
  accepted: 'Accepted',
  shipped: 'Shipped',
  order_completed: 'Order Completed',
  cancelled: 'Cancelled',
};

export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  draft: '#6b7280',
  submission_failed: '#dc2626',
  submitted_to_sage: '#06b6d4',
  confirmed_in_sage: '#2563eb',
  accepted: '#059669',
  shipped: '#0d9488',
  order_completed: '#16a34a',
  cancelled: '#ef4444',
};

export const ORDER_ACTION_LABELS: Record<string, string> = {
  submit: 'Submit to Sage',
  confirm_sage: 'Confirm in Sage',
  // Undo of a MISTAKEN manual confirm (order never in Sage); the portal
  // only offers it when the server judged the order eligible.
  unconfirm_sage: 'Undo confirm',
  accept: 'Mark Accepted',
  complete: 'Mark Completed',
  // No 'cancel' label: the action is no longer offered anywhere in the UI.
  // See the note on STATUS_ACTIONS below.
};

export const ORDER_SOURCE_LABELS: Record<OrderSourceType, string> = {
  email_llm: 'Email/LLM',
  manual: 'Manual',
  quote_conversion: 'Quote',
};

// ── Timeline types ────────────────────────────────────────────────────

export type TimelineEventType =
  | 'email_received'
  | 'order_created'
  | 'state_transition'
  | 'bol_attached'
  | 'bol_scanned_out'
  | 'bol_shipped_via_portal'
  | 'bol_scan_trigger'
  | 'sage_import_synced'
  | 'sage_order_synced'
  | 'order_updated'
  | 'hold_changed'
  | 'error';

export interface TimelineEvent {
  event_type: TimelineEventType;
  timestamp: string;
  title: string;
  description: string | null;
  actor: string | null;
  icon: string;
  color: string;
  metadata: Record<string, unknown> | null;
}

export interface OrderTimelineResponse {
  order_id: string;
  current_status: OrderStatus;
  events: TimelineEvent[];
  event_count: number;
}

export interface LatestTimelineEvent {
  order_id: string;
  title: string;
  timestamp: string;
  icon: string;
  color: string;
  event_type: TimelineEventType;
}

export interface LatestEventsResponse {
  events: Record<string, LatestTimelineEvent>;
}

// Map status to allowed actions.
//
// 'cancel' is deliberately absent from every row. The backend transition
// still accepts it — the stale-draft cleanup sweep and one-off remediation
// scripts call OrderService.cancel directly — but it only moves OUR record to
// CANCELLED and never touches the Sage sales order. Offered as a button it
// reads as "cancel this order", so a CSR would mark the order cancelled here
// while the Sage SO stayed open and shipped. That is the same trap PR #1926
// removed from the portal's OrderDetailModal; this map was the surviving copy
// on the admin Orders page.
//
// Do not add it back until a real Sage-side cancel exists (kanban e2ed881e:
// blocked on a cancel verb in Adam's cmsync/sync-so-updates.php drain).
// Guarded by orders.statusActions.test.ts.
export const STATUS_ACTIONS: Record<OrderStatus, string[]> = {
  draft: ['submit'],
  submission_failed: ['submit'],
  submitted_to_sage: ['confirm_sage', 'accept', 'complete'],
  confirmed_in_sage: ['accept', 'complete'],
  accepted: ['complete'],
  // Entered automatically when the warehouse scans the BOL — no manual
  // "ship" action exists; only completion remains.
  shipped: ['complete'],
  order_completed: [],
  cancelled: [],
};
